/**
 * Nothing from a URL's query string or fragment reaches Sentry.
 *
 * The reset-password link carries its token in the query string, and the
 * browse page puts the search text there (as do its API calls). The browser
 * SDK sends the page URL with every error, records navigation and fetch URLs
 * as breadcrumbs, and Session Replay records the address in its own events
 * (the snapshot "meta" event holds location.href, which no Replay hook can
 * edit). So every envelope is scrubbed in the transport, the last step before
 * it leaves the browser: errors, breadcrumbs, the replay event and the replay
 * recording alike. Replay compression is off (main.tsx) so the recording is
 * text that can be scrubbed; a compressed recording is dropped, not sent.
 */

// A URL (absolute, protocol-relative or a path starting with "/") at the start
// of a string or after a separator, followed by ?query or #fragment. The
// separator is captured rather than looked behind, for older Safari.
const URL_WITH_QUERY =
  /(^|[\s("'=,[{<>])((?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s?#"'<>()]*)[?#][^\s"'<>()]*/gi;
// Secret-looking parameters anywhere else, e.g. a relative "reset-password?token=…".
const SECRET_PARAM =
  /([?&;]|^)((?:reset_|access_|refresh_)?token|code|key|secret|password|sig|signature)=[^&\s"'<>#]*/gi;

export function scrubString(text: string): string {
  if (text.startsWith("?")) return "";
  return text.replace(URL_WITH_QUERY, "$1$2").replace(SECRET_PARAM, "$1$2=[Filtered]");
}

/** Every string in a JSON-like value, scrubbed; other values unchanged. */
export function scrubValue<T>(value: T, seen = new WeakSet<object>()): T {
  if (typeof value === "string") return scrubString(value) as T;
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, seen)) as T;
  // Events are normalised to plain JSON before they reach the transport.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = scrubValue(v, seen);
  return out as T;
}

type EnvelopeItem = [Record<string, unknown>, unknown];
type Envelope = [Record<string, unknown>, EnvelopeItem[]];

/** A replay recording item: a JSON header line, then a JSON array of rrweb events. */
function scrubRecording(payload: string): string {
  const newline = payload.indexOf("\n");
  const header = newline === -1 ? "" : payload.slice(0, newline + 1);
  const events = JSON.parse(newline === -1 ? payload : payload.slice(newline + 1));
  return `${header}${JSON.stringify(scrubValue(events))}`;
}

export function scrubEnvelope(envelope: Envelope): Envelope {
  const [headers, items] = envelope;
  const scrubbed: EnvelopeItem[] = [];
  for (const [itemHeader, payload] of items) {
    if (itemHeader.type === "replay_recording") {
      // Compressed (or unreadable) recordings cannot be checked: drop them.
      if (typeof payload !== "string") continue;
      let recording: string;
      try {
        recording = scrubRecording(payload);
      } catch {
        continue;
      }
      scrubbed.push([{ ...itemHeader, length: new TextEncoder().encode(recording).length }, recording]);
      continue;
    }
    if (payload instanceof Uint8Array) {
      scrubbed.push([itemHeader, payload]);
      continue;
    }
    const clean = scrubValue(payload);
    scrubbed.push([
      typeof clean === "string" && typeof itemHeader.length === "number"
        ? { ...itemHeader, length: new TextEncoder().encode(clean).length }
        : itemHeader,
      clean,
    ]);
  }
  return [scrubValue(headers), scrubbed];
}

type Transport = {
  send(envelope: any): PromiseLike<unknown>; // the SDK's own envelope type
  flush(timeout?: number): PromiseLike<boolean>;
};

/** Wraps the SDK's transport so that every envelope is scrubbed before it is sent. */
export function scrubbingTransport<O, T extends Transport>(makeTransport: (options: O) => T) {
  return (options: O): T => {
    const transport = makeTransport(options);
    return {
      send: (envelope: Envelope) => transport.send(scrubEnvelope(envelope)),
      flush: (timeout?: number) => transport.flush(timeout),
    } as T;
  };
}
